import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Upload, ImagePlus, RefreshCw } from "lucide-react";
import { api, CATEGORIES, SHOWS, FILE_BASE } from "@/lib/api";
import { useUploadAccess } from "@/lib/uploadAccess";
import { toast } from "sonner";

const THUMBNAIL_ACCEPT = "image/*,video/mp4,video/webm,video/quicktime,video/x-m4v";

const initial = {
  title: "",
  description: "",
  category: "Overlays",
  creator_tag: "",
  ae_version: "",
  bpm: "",
  genre: "",
  show_group: "",
  torrent_type: "Show",
  thumbnail_url: "",
  file_url: "",
  original_filename: "",
  external_url: "",
  is_updated: false,
  updated_at: "",
};

const UPDATE_MARKABLE_CATEGORIES = new Set(["Overlays", "Sound FX"]);
const CUSTOM_CREATOR_VALUE = "__new_creator__";

const isVideoPreview = (url = "") => /\.(mp4|webm|mov|m4v)(?:[?#]|$)/i.test(url);

const isYouTubeUrl = (url = "") =>
  /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//i.test(url.trim());

const isTypingTarget = (target) => {
  if (!target?.closest) return false;
  return Boolean(target.closest("input, textarea, select, [contenteditable='true'], [role='textbox']"));
};

async function compressThumbnail(file) {
  if (!file?.type?.startsWith("image/")) return file;
  if (file.type === "image/gif") return file;

  const imageUrl = URL.createObjectURL(file);

  try {
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = imageUrl;
    });

    const maxWidth = 900;
    const scale = Math.min(1, maxWidth / img.width);
    const width = Math.round(img.width * scale);
    const height = Math.round(img.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.82)
    );

    if (!blob) return file;

    return new File(
      [blob],
      file.name.replace(/\.[^.]+$/, "") + "-thumbnail.jpg",
      { type: "image/jpeg" }
    );
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

function uploadToPresignedUrl(uploadUrl, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType || file.type || "application/octet-stream");
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress?.(Math.max(1, Math.round((event.loaded / event.total) * 100)));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Direct upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Direct upload failed"));
    xhr.send(file);
  });
}

export default function UploadModal({ open, onOpenChange, editing, onSaved }) {
  const { canDelete } = useUploadAccess();
  const [form, setForm] = useState(editing ? { ...initial, ...editing, genre: editing.genre || editing.bpm || "", bpm: "" } : initial);
  const [uploadingField, setUploadingField] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [knownCreators, setKnownCreators] = useState([]);
  const [knownShows, setKnownShows] = useState([]);
  const [creatorOverrides, setCreatorOverrides] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadDistincts = async () => {
    const [cr, audioAssets, sh, ov] = await Promise.allSettled([
      api.get("/distinct/creators"),
      api.get("/assets", { params: { category: "Audios" } }),
      api.get("/distinct/shows"),
      api.get("/category-overrides", { params: { kind: "creator" } }),
    ]);
    const distinctCreators = cr.status === "fulfilled" ? cr.value.data || [] : [];
    const creatorsFromAudioCategories = audioAssets.status === "fulfilled"
      ? (audioAssets.value.data || []).map((asset) => asset.creator_tag).filter(Boolean)
      : [];
    const grouped = {};
    if (ov.status === "fulfilled") {
      for (const item of ov.value.data || []) grouped[item.name] = item;
    }
    setKnownCreators(Array.from(new Set([...distinctCreators, ...creatorsFromAudioCategories, ...Object.keys(grouped)])));
    setKnownShows(sh.status === "fulfilled" ? sh.value.data || [] : []);
    setCreatorOverrides(grouped);
  };

  useEffect(() => {
    if (open) loadDistincts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setCategory = (v) => {
    set("category", v);
  };

  const uploadViaBackend = async (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event) => {
        if (!event.total) return;
        onProgress?.(Math.max(1, Math.round((event.loaded / event.total) * 100)));
      },
    });
    return data;
  };

  const uploadAssetFile = async (file, onProgress) => {
    try {
      const { data: presign } = await api.post("/uploads/presign", {
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        size: file.size,
      });
      await uploadToPresignedUrl(
        presign.upload_url,
        file,
        presign.content_type,
        onProgress
      );
      return presign;
    } catch (directError) {
      return uploadViaBackend(file, onProgress);
    }
  };

  const uploadFile = async (file, field) => {
    setUploadingField(field);
    setUploadProgress(0);
    try {
      const data = await uploadAssetFile(file, setUploadProgress);

      setForm((f) => ({
        ...f,
        [field]: data.url,
        ...(field === "file_url" ? { original_filename: data.original_filename || file.name } : {}),
      }));
      setUploadProgress(100);
      toast.success("File uploaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadingField("");
      setUploadProgress(0);
    }
  };

  const uploadThumbnail = async (file) => {
    const compressed = await compressThumbnail(file);
    await uploadFile(compressed, "thumbnail_url");
  };

  const handleThumbnailPaste = async (e) => {
    const file = Array.from(e.clipboardData?.files || []).find((f) =>
      f.type.startsWith("image/") || f.type.startsWith("video/")
    );

    if (!file) return;

    e.preventDefault();
    await uploadThumbnail(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required.");
    if (form.category === "Videos" && !isYouTubeUrl(form.external_url))
      return toast.error("Videos need a valid YouTube link.");
    if (!form.file_url && !form.external_url)
      return toast.error("Provide a file upload or external link.");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        file_url: form.category === "Videos" ? "" : form.file_url,
        original_filename: form.category === "Videos" ? "" : form.original_filename,
        bpm: "",
        genre: form.genre || form.bpm || "",
        ...(editing?.id && !canMarkUpdated ? { is_updated: false, updated_at: "" } : {}),
      };
      if (editing?.id) {
        await api.patch(`/assets/${editing.id}`, payload);
        toast.success("Asset updated.");
      } else {
        await api.post("/assets", payload);
        toast.success("Asset published.");
      }
      onSaved?.();
      onOpenChange(false);
      setForm(initial);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    }
    setSubmitting(false);
  };

  const isAudio = form.category === "Audios";
  const isVideo = form.category === "Videos";
  const isPF = form.category === "Project Files";
  const isTorrent = form.category === "Torrents";
  const categoryOptions = canDelete ? CATEGORIES : CATEGORIES.filter((c) => c !== "Premium");
  const canMarkUpdated = Boolean(editing?.id) && UPDATE_MARKABLE_CATEGORIES.has(form.category);
  const creatorSuggestions = Array.from(
    new Set([...knownCreators, ...Object.keys(creatorOverrides)].filter(Boolean))
  )
    .filter((name) => !creatorOverrides[name]?.deleted)
    .sort((a, b) => a.localeCompare(b));
  const creatorSelectValue =
    form.creator_tag && creatorSuggestions.includes(form.creator_tag)
      ? form.creator_tag
      : CUSTOM_CREATOR_VALUE;
  const showCustomCreatorInput = creatorSelectValue === CUSTOM_CREATOR_VALUE;
  const thumbnailPreviewSrc = form.thumbnail_url
    ? form.thumbnail_url.startsWith("http") ? form.thumbnail_url : `${FILE_BASE}${form.thumbnail_url}`
    : "";

  const uploadLabel = (field, ready) => {
    if (uploadingField !== field) return ready ? "File ready ✓" : "Click to upload";
    return uploadProgress ? `Uploading ${uploadProgress}%...` : "Preparing upload...";
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (v) {
          loadDistincts();
        }
      }}
    >
      <DialogContent
        className="glass border-[var(--line)] text-ink max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="upload-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {editing ? "Edit Asset" : "Upload Asset"}
          </DialogTitle>
          <DialogDescription className="text-ink-2">
            {isVideo
              ? "Paste a YouTube link and it will play directly inside the Videos tab."
              : "Pick a category, drop a thumbnail, and add one file or external link."}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={submit}
          onKeyDownCapture={(event) => {
            if ((event.key === " " || event.code === "Space") && isTypingTarget(event.target)) {
              event.stopPropagation();
            }
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-ink-3">
                Title
              </label>
              <Input
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder={isVideo ? "Video title" : "Asset title"}
                className="bg-[var(--fill)] border-[var(--line)] mt-1"
                data-testid="upload-title-input"
              />
            </div>

            <div className="col-span-2">
              <label className="text-xs font-semibold text-ink-3">
                Category
              </label>
              <Select value={form.category} onValueChange={setCategory}>
                <SelectTrigger
                  className="bg-[var(--fill)] border-[var(--line)] mt-1"
                  data-testid="upload-category-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass border-[var(--line)] text-ink">
                  {categoryOptions.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {canMarkUpdated && (
              <label className="col-span-2 group cursor-pointer rounded-xl border border-amber-300/20 bg-amber-300/5 px-4 py-3 flex items-start gap-3 hover:bg-amber-300/10 transition-colors">
                <span className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center transition-all ${form.is_updated ? "bg-amber-300 border-amber-200 text-black shadow-[0_0_18px_rgba(251,191,36,0.25)]" : "border-[var(--line-strong)] bg-[var(--fill)] text-transparent"}`}>
                  <RefreshCw className="w-3.5 h-3.5" />
                </span>
                <input
                  type="checkbox"
                  checked={Boolean(form.is_updated)}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    is_updated: e.target.checked,
                    updated_at: e.target.checked ? f.updated_at : "",
                  }))}
                  className="sr-only"
                  data-testid="upload-updated-checkbox"
                />
                <span>
                  <span className="block text-sm font-semibold text-amber-100">
                    Mark this asset as updated
                  </span>
                  <span className="block text-xs text-ink-2 mt-1">
                    Use this when the existing Drive link now contains a new overlay or sound effect. It will move back into Recently Added and show an UPDATED tag.
                  </span>
                </span>
              </label>
            )}

            <div className="col-span-2">
              <label className="text-xs font-semibold text-ink-3">
                Description
              </label>
              <Textarea
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                rows={3}
                className="bg-[var(--fill)] border-[var(--line)] mt-1 resize-none"
                data-testid="upload-description-input"
              />
            </div>

            {isAudio && (
              <>
                <div>
                  <label className="text-xs font-semibold text-ink-3">
                    Creator
                  </label>
                  <Select
                    value={creatorSelectValue}
                    onValueChange={(value) => {
                      set("creator_tag", value === CUSTOM_CREATOR_VALUE ? "" : value);
                    }}
                  >
                    <SelectTrigger
                      className="bg-[var(--fill)] border-[var(--line)] mt-1"
                      data-testid="upload-creator-select"
                    >
                      <SelectValue placeholder="Choose a creator" />
                    </SelectTrigger>
                    <SelectContent className="glass border-[var(--line)] text-ink">
                      {creatorSuggestions.map((creator) => (
                        <SelectItem key={creator} value={creator}>
                          {creator}
                        </SelectItem>
                      ))}
                      <SelectItem value={CUSTOM_CREATOR_VALUE}>
                        New creator / type one
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {showCustomCreatorInput && (
                    <Input
                      value={form.creator_tag}
                      onChange={(e) => set("creator_tag", e.target.value)}
                      placeholder="Type a new creator name"
                      className="bg-[var(--fill)] border-[var(--line)] mt-2"
                      data-testid="upload-creator-input"
                    />
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-ink-3">
                    Genre
                  </label>
                  <Input
                    value={form.genre || form.bpm || ""}
                    onChange={(e) => set("genre", e.target.value)}
                    placeholder="e.g. phonk, cinematic, dark"
                    className="bg-[var(--fill)] border-[var(--line)] mt-1"
                    data-testid="upload-genre-input"
                  />
                </div>
              </>
            )}

            {!isAudio && (
              <div className="col-span-2">
                <label className="text-xs font-semibold text-ink-3">
                  Creator Tag
                </label>
                <Input
                  value={form.creator_tag}
                  onChange={(e) => set("creator_tag", e.target.value)}
                  placeholder="e.g. mrbit_100"
                  className="bg-[var(--fill)] border-[var(--line)] mt-1"
                />
              </div>
            )}

            {isPF && (
              <div>
                <label className="text-xs font-semibold text-ink-3">
                  AE Version
                </label>
                <Input
                  value={form.ae_version}
                  onChange={(e) => set("ae_version", e.target.value)}
                  placeholder="e.g. After Effects 2024"
                  className="bg-[var(--fill)] border-[var(--line)] mt-1"
                />
              </div>
            )}

            {isTorrent && (
              <>
                <div>
                  <label className="text-xs font-semibold text-ink-3">
                    Type
                  </label>
                  <Select
                    value={form.torrent_type || "Show"}
                    onValueChange={(v) => set("torrent_type", v)}
                  >
                    <SelectTrigger className="bg-[var(--fill)] border-[var(--line)] mt-1" data-testid="upload-torrent-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="glass border-[var(--line)] text-ink">
                      <SelectItem value="Show">Show</SelectItem>
                      <SelectItem value="Movie">Movie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.torrent_type !== "Movie" && (
                  <div>
                    <label className="text-xs font-semibold text-ink-3">
                      Show (type to add a new one)
                    </label>
                    <Input
                      list="show-suggestions"
                      value={form.show_group}
                      onChange={(e) => set("show_group", e.target.value)}
                      placeholder="e.g. The Boys or your own"
                      className="bg-[var(--fill)] border-[var(--line)] mt-1"
                      data-testid="upload-show-input"
                    />
                    <datalist id="show-suggestions">
                      {[
                        ...SHOWS,
                        ...knownShows.filter((s) => !SHOWS.includes(s)),
                      ].map((s) => (
                        <option key={s} value={s} />
                      ))}
                    </datalist>
                  </div>
                )}
              </>
            )}

            <div className="col-span-2" onPaste={handleThumbnailPaste}>
              <label className="text-xs font-semibold text-ink-3">
                Thumbnail / Preview URL
              </label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={form.thumbnail_url}
                  onChange={(e) => set("thumbnail_url", e.target.value)}
                  placeholder="https://... or upload image, GIF, MP4"
                  className="bg-[var(--fill)] border-[var(--line)]"
                  data-testid="upload-thumbnail-url"
                />
                <label className="cursor-pointer bg-[var(--fill)] border border-[var(--line)] px-3 rounded-md flex items-center hover:bg-[var(--fill-2)] btn-press">
                  <ImagePlus className="w-4 h-4" />
                  <input
                    type="file"
                    accept={THUMBNAIL_ACCEPT}
                    hidden
                    onChange={(e) =>
                      e.target.files?.[0] &&
                      uploadThumbnail(e.target.files[0])
                    }
                  />
                </label>
              </div>
              {uploadingField === "thumbnail_url" && (
                <p className="mt-1 text-xs text-ink-2">
                  {uploadProgress ? `Uploading preview ${uploadProgress}%...` : "Preparing preview upload..."}
                </p>
              )}
              <p className="mt-1 text-xs text-ink-3">
                Supports JPG/PNG, animated GIF, and MP4/WebM previews. Snipping Tool paste still works for images.
              </p>
              {thumbnailPreviewSrc && (
                isVideoPreview(thumbnailPreviewSrc) ? (
                  <video
                    src={thumbnailPreviewSrc}
                    className="mt-2 w-full max-h-44 object-cover rounded-lg border border-[var(--line)] bg-[var(--fill)]"
                    muted
                    loop
                    playsInline
                    autoPlay
                    controls
                  />
                ) : (
                  <img
                    src={thumbnailPreviewSrc}
                    alt=""
                    className="mt-2 w-full max-h-44 object-cover rounded-lg border border-[var(--line)]"
                  />
                )
              )}
            </div>

            <div className={`col-span-2 grid ${isVideo ? "grid-cols-1" : "grid-cols-2"} gap-3 mt-2 pt-3 border-t border-[var(--line)]`}>
              {!isVideo && (
              <div>
                <label className="text-xs font-semibold text-ink-3">
                  File Upload (≤ 50MB)
                </label>
                <label className="mt-1 flex items-center justify-center gap-2 h-11 cursor-pointer bg-[var(--fill)] border border-dashed border-[var(--line)] rounded-md hover:bg-[var(--fill-2)] btn-press text-sm text-ink-2">
                  <Upload className="w-4 h-4" />
                  {uploadLabel("file_url", Boolean(form.file_url))}
                  <input
                    type="file"
                    hidden
                    onChange={(e) =>
                      e.target.files?.[0] && uploadFile(e.target.files[0], "file_url")
                    }
                  />
                </label>
              </div>
              )}
              <div>
                <label className="text-xs font-semibold text-ink-3">
                  {isVideo ? "YouTube URL" : "External URL (Drive / Mega)"}
                </label>
                <Input
                  value={form.external_url}
                  onChange={(e) => set("external_url", e.target.value)}
                  placeholder={isVideo ? "https://www.youtube.com/watch?v=..." : "https://drive.google.com/..."}
                  className="bg-[var(--fill)] border-[var(--line)] mt-1"
                  data-testid="upload-external-url"
                />
                {isVideo && (
                  <p className="mt-1 text-xs text-ink-3">
                    YouTube, youtu.be, Shorts, and embed links are supported.
                  </p>
                )}
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting || Boolean(uploadingField)}
            className="w-full bg-neon text-[#05050A] hover:bg-neon/90 font-semibold btn-press h-11"
            data-testid="upload-submit-btn"
          >
            {submitting ? "Saving..." : editing ? "Save Changes" : "Publish Asset"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
